import { useEffect, useState } from "react";
import {
	Modal,
	Collapse,
	Table,
	Badge,
	Avatar,
	Button,
	Select,
	Input,
	Spin,
	Empty,
} from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import trans from "../../../utils";
import { useGetServiceRequestTasksOpenQuery } from "../../../Redux/service/Dispatcher/Dispatcher";
import { useGetAllSkusQuery } from "../../../Redux/service/Sku/Sku";
import { EgyptGovernorates } from "../../../constants";
import { getMarkazOptions } from "../../../utils/CountryState/egyptMarkaz";

function formatVisitWindow(start, end) {
	if (!start) return "-";
	const startText = dayjs(start).format("hh:mm A");
	return end ? `${startText} – ${dayjs(end).format("hh:mm A")}` : startText;
}

export default function OpenRequestsPickerModal({
	open,
	onClose,
	poiIds,
	initialSelectedIds,
	onNext,
}) {
	const [checkedIds, setCheckedIds] = useState(initialSelectedIds || []);
	const [search, setSearch] = useState("");
	const [governorate, setGovernorate] = useState([]);
	const [markaz, setMarkaz] = useState([]);
	const [productId, setProductId] = useState([]);

	useEffect(() => {
		if (open) {
			setCheckedIds(initialSelectedIds || []);
			setSearch("");
			setGovernorate([]);
			setMarkaz([]);
			setProductId([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const { data: skusData } = useGetAllSkusQuery();
	const productOptions = (skusData?.data || []).map((s) => ({
		label: s.skuName,
		value: String(s.id),
	}));
	const markazOptions = governorate.length
		? Array.from(
				new Map(
					governorate
						.flatMap((g) => getMarkazOptions(g))
						.map((o) => [o.value, o]),
				).values(),
			)
		: [];

	const { data, isFetching: loading } = useGetServiceRequestTasksOpenQuery(
		{
			poiId: poiIds || [],
			search: search || undefined,
			governorate: governorate.length ? governorate : undefined,
			markaz: markaz.length ? markaz : undefined,
			productId: productId.length ? productId : undefined,
		},
		{ skip: !open || !poiIds?.length, refetchOnMountOrArgChange: true },
	);
	const groups = data?.data || [];

	const handleNext = () => {
		const pickedExtraRequests = groups.flatMap((group) => group.requests || []);
		onNext(checkedIds, pickedExtraRequests);
	};

	const columns = [
		{
			title: trans("Order No"),
			dataIndex: "requestNumber",
			key: "requestNumber",
			width: 140,
		},
		{
			title: trans("Product"),
			dataIndex: "product",
			key: "product",
			width: 180,
			ellipsis: true,
		},
		{
			title: trans("Governorate"),
			dataIndex: "governorate",
			key: "governorate",
			width: 110,
			render: (v) => (v ? trans(v) : "-"),
		},
		{
			title: trans("Zone"),
			dataIndex: "markaz",
			key: "markaz",
			width: 110,
			render: (v) => (v ? trans(v) : "-"),
		},
		{
			title: trans("Address"),
			dataIndex: "address",
			key: "address",
			width: 200,
			ellipsis: true,
		},
		{
			title: trans("Preferred Maintenance Time"),
			key: "preferredVisit",
			width: 170,
			render: (_, r) =>
				formatVisitWindow(r.preferredVisitTime, r.preferredVisitTimeEnd),
		},
	];

	const items = groups.map((group) => {
		const requestIds = (group.requests || []).map((r) => r.id);
		const selectedInGroup = requestIds.filter((id) => checkedIds.includes(id));
		return {
			key: group.poiId,
			label: (
				<div
					className="d-flex align-items-center justify-content-between"
					style={{ width: "100%" }}
				>
					<div className="d-flex align-items-center gap-2">
						<Avatar
							icon={<UserOutlined />}
							style={{ backgroundColor: "#389741" }}
						/>
						<span className="fw-500">{group.poiName}</span>
					</div>
					<Badge
						count={group.requests?.length || 0}
						style={{ backgroundColor: "#0f6936" }}
						showZero
					/>
				</div>
			),
			// Plain CSS max-height instead of antd's scroll.y — scroll.y forces a
			// fixed-height body even for 2-3 rows; this lets the table size to its
			// content and only clip/scroll once it actually exceeds the cap.
			children: requestIds.length ? (
				<div style={{ maxHeight: 240, overflowY: "auto" }}>
					<Table
						rowKey="id"
						size="small"
						pagination={false}
						columns={columns}
						dataSource={group.requests}
						scroll={{ x: "max-content" }}
						rowSelection={{
							selectedRowKeys: selectedInGroup,
							onChange: (keys) => {
								setCheckedIds((prev) => [
									...prev.filter((id) => !requestIds.includes(id)),
									...keys,
								]);
							},
						}}
					/>
				</div>
			) : (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={trans("No open requests")}
				/>
			),
		};
	});

	return (
		<Modal
			title={trans("Assign Open Requests")}
			open={open}
			onCancel={onClose}
			width={920}
			footer={[
				<Button key="cancel" onClick={onClose}>
					{trans("Cancel")}
				</Button>,
				<Button
					key="next"
					type="primary"
					onClick={handleNext}
					disabled={!checkedIds.length}
				>
					{trans("Next")}
				</Button>,
			]}
		>
			<p className="neutral-400 mb-3">
				{trans(
					"Select the requests that will be handled by the same field staff member in a single visit.",
				)}
			</p>
			<div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
				<Input
					prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
					placeholder={trans("Search")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{ width: 220 }}
					allowClear
				/>
				<Select
					mode="multiple"
					allowClear
					placeholder={trans("Governorate")}
					style={{ width: 180 }}
					options={EgyptGovernorates}
					value={governorate}
					onChange={(value) => {
						setGovernorate(value);
						setMarkaz([]);
					}}
					maxTagCount="responsive"
				/>
				<Select
					mode="multiple"
					allowClear
					placeholder={trans("Zone")}
					style={{ width: 180 }}
					options={markazOptions}
					value={markaz}
					onChange={setMarkaz}
					maxTagCount="responsive"
				/>
				<Select
					mode="multiple"
					allowClear
					placeholder={trans("Product")}
					style={{ width: 180 }}
					options={productOptions}
					value={productId}
					onChange={setProductId}
					maxTagCount="responsive"
				/>
			</div>
			<div style={{ maxHeight: "55vh", overflowY: "auto" }}>
				{loading ? (
					<div className="text-center py-4">
						<Spin />
					</div>
				) : items.length ? (
					<Collapse items={items} defaultActiveKey={[items[0]?.key]} />
				) : (
					<Empty description={trans("No related open requests found")} />
				)}
			</div>
		</Modal>
	);
}
